// Ported from LC2's ConcessionComps.js + ConcessionCompsInfo.js (commons/components/
// HomeTabs/Performance/Metrics/) — same campaign-level (`getConcessionSavingByCampaignSfid`
// + `updateConcessionSavingByCampaignSfid`) and property-level (`getConcessionSavingByPropertySfid`
// + `updateConcessionSavingByPropertySfid`) fetch/update pair, same visibility gate
// (campaign/community.hidden_loyalty_cloud_features__c, always visible for a Stake admin),
// same Draft-campaign special case, same concessionData merge-precedence (apply-mutation
// result > success-query result), same editable market-rent/zip/asset-class filter chips with
// an Apply button, same before/after "Savings" panels. This only renders from
// CampaignPerformance.js in this port (see that file) — the property-level branch is kept
// for parity with LC2's component but is currently unreachable here since CommunityPerformance
// doesn't render this component (LC2 has it commented out there too — see that file's header
// comment), and the property query is skipped whenever `campaign_sfid` is present anyway.
// SIMPLIFIED (no chart here — this component never used recharts/d3):
// - LC2's editable chips used `react-number-format` masked inputs (MoneyFormatCustom/
//   PositiveNumberFormat) and validated the zip edit against a large hardcoded
//   `validZipCodes` list. Neither `react-number-format` nor that constants file exist in
//   this app; the edits are plain MUI TextFields instead (a `$`-prefixed/thousands-formatted
//   display re-derived on blur for the rent field, a basic 5-digit-numeric check for the
//   zip field instead of the hardcoded valid-zip list) — same edited-value shape is still
//   sent to the same mutation.
// - The FontAwesome pencil/question-mark icons are swapped for MUI icons (no FontAwesome
//   dependency in this app).
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {Box, Button, Chip, Menu, MenuItem, Stack, TextField, Tooltip, Typography} from '@mui/material';
import {EditOutlined, HelpOutlineOutlined} from '@mui/icons-material';

import {tokens} from '../../../themes/ThemeCustomization';
import {getDashboardAccount, getDashboardCampaign, getDashboardCommunity} from '../../../app/store/reducers/dashboard';
import {getCampaigns, getCommunities} from '../../../app/store/reducers/entities';
import {getIsStakeAdmin} from '../../../app/store/reducers/session';
import {getOpenItem} from '../../../app/store/reducers/menu';
import {
    useGetConcessionSavingByCampaignSfidQuery,
    useGetConcessionSavingByPropertySfidQuery,
    useUpdateConcessionSavingByCampaignSfidMutation,
    useUpdateConcessionSavingByPropertySfidMutation
} from '../../../features/coreApi/metricsSlice';
import LoadingBox from '../../../commons/components/LoadingBox';
import VisibilitySwitch from './VisibilitySwitch';

const ConcessionsHeader = ({data = {}}) => (
    <Stack direction="column">
        <Typography variant="h5" fontWeight="fontWeightMedium">
            {data.title}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
            {data.description}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
            This data is sourced from publicly available online listings.
        </Typography>
    </Stack>
);

const EditableChip = ({label, nonEditablePart, format, setEditedValues, editedValues}) => {
    const [value, setValue] = useState(label);
    const [error, setError] = useState(null);

    const handleBlur = (e) => {
        const raw = e.target.value;
        if (format === 'zip_code') {
            if (!/^\d{5}$/.test(raw)) {
                setError('Invalid zip code');
                return;
            }
            setError(null);
            setEditedValues({...editedValues, [format]: raw});
        } else {
            const parsed = Number(String(raw).replace(/[$,]/g, ''));
            if (Number.isNaN(parsed)) {
                setError('Invalid value');
                return;
            }
            setError(null);
            setEditedValues({...editedValues, [format]: parsed});
        }
    };

    return (
        <Stack direction="column" rowGap={0.5} alignItems="center">
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    backgroundColor: tokens.color.neutral100,
                    borderRadius: '16px',
                    px: 1.5,
                    height: '24px'
                }}
            >
                <Typography variant="caption">{nonEditablePart}</Typography>
                <TextField
                    variant="standard"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={handleBlur}
                    InputProps={{disableUnderline: true, style: {fontSize: '0.8125rem', width: format === 'market_rent' ? 66 : 44}}}
                />
                <EditOutlined sx={{fontSize: 14, color: tokens.color.neutral500}} />
            </Box>
            {error && (
                <Typography variant="caption" color="error.main">
                    {error}
                </Typography>
            )}
        </Stack>
    );
};

const EditableChipDropdown = ({label, setEditedValues, format, editedValues, options = []}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [assetClass, setAssetClass] = useState(label);

    const handleSelect = (option) => {
        setAssetClass(option);
        setEditedValues({...editedValues, [format]: option});
        setAnchorEl(null);
    };

    return (
        <>
            <Button
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{backgroundColor: tokens.color.neutral100, borderRadius: '16px', px: 1.5, height: '24px', fontSize: '0.8125rem'}}
                endIcon={<EditOutlined sx={{fontSize: '14px !important'}} />}
            >
                {assetClass}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {options.map((option) => (
                    <MenuItem key={option} onClick={() => handleSelect(option)}>
                        {option}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

const ConcessionsInfo = ({data = {}, editedValues, setEditedValues, handleApply}) => {
    const {filters = [], title, market_rent, asset_class, asset_classes_dropdown = [], zip_code = ''} = data;
    const isButtonActivated = Object.values(editedValues).length > 0;
    const [zipLabel, zipValue] = zip_code.split(':');

    return (
        <Stack direction="column" rowGap={1}>
            <Typography variant="subtitle1">{title}</Typography>
            <Stack direction="row" columnGap={1} rowGap={1} flexWrap="wrap" alignItems="center">
                {filters.map((criteria) => (
                    <Chip key={criteria} label={criteria} size="small" />
                ))}
                <EditableChip
                    label={market_rent}
                    nonEditablePart="Market rent:"
                    format="market_rent"
                    setEditedValues={setEditedValues}
                    editedValues={editedValues}
                />
                <EditableChip
                    label={zipValue}
                    nonEditablePart={zipLabel}
                    format="zip_code"
                    setEditedValues={setEditedValues}
                    editedValues={editedValues}
                />
                <EditableChipDropdown
                    label={asset_class}
                    options={asset_classes_dropdown}
                    setEditedValues={setEditedValues}
                    format="asset_class"
                    editedValues={editedValues}
                />
                <Button disabled={!isButtonActivated} variant="contained" color="secondary" size="small" onClick={handleApply}>
                    Apply
                </Button>
            </Stack>
        </Stack>
    );
};

const Savings = ({data = [], mainInfo = {}}) => (
    <Stack direction="column" rowGap={2}>
        <Stack direction="column">
            <Typography variant="h6">{mainInfo.title}</Typography>
            <Typography variant="subtitle2" color="text.secondary">
                {mainInfo.description}
            </Typography>
        </Stack>
        <Stack direction="row" columnGap={1} flexWrap="wrap">
            {data.map((block) => (
                <Box key={block.name} sx={{flex: '1 1 240px', border: `1px solid ${tokens.color.neutral200}`, borderRadius: '10px', p: 2}}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="column">
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="h6">{block.name}</Typography>
                                {block.tooltip && (
                                    <Tooltip title={block.tooltip} arrow>
                                        <HelpOutlineOutlined sx={{fontSize: 14, color: tokens.color.neutral400}} />
                                    </Tooltip>
                                )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {block.description}
                            </Typography>
                        </Stack>
                        <Chip label={block.value} size="small" sx={{backgroundColor: block.color}} />
                    </Stack>
                </Box>
            ))}
        </Stack>
    </Stack>
);

const ConcessionComps = () => {
    const params = useParams();
    const openItem = useSelector(getOpenItem);
    const dashboardAccount = useSelector(getDashboardAccount);
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const [visible, setVisible] = useState(false);
    const [editedValues, setEditedValues] = useState({});

    const dashboardCampaign = useSelector(getDashboardCampaign);
    const campaigns = useSelector(getCampaigns);
    const campaign = params?.campaign_sfid && campaigns.find((c) => c.id === dashboardCampaign.sfid);

    const {data, isSuccess, isError, isFetching, isLoading} = useGetConcessionSavingByCampaignSfidQuery(params?.campaign_sfid, {
        skip: !params?.campaign_sfid || (!visible && !isStakeAdmin)
    });
    const [updateConcessionSavingByCampaignSfid, {data: updatedValues, isSuccess: isApplySuccess, isLoading: isApplyLoading, isFetching: isApplyFetching}] =
        useUpdateConcessionSavingByCampaignSfidMutation();

    const dashboardCommunity = useSelector(getDashboardCommunity);
    const communities = useSelector(getCommunities);
    const community = openItem === 'communities' && communities.find((c) => c.sfid === dashboardCommunity.sfid);

    const {
        data: propertyData,
        isSuccess: isPropertySuccess,
        isLoading: isPropertyLoading,
        isFetching: isPropertyFetching,
        isError: isPropertyError
    } = useGetConcessionSavingByPropertySfidQuery(params?.community_sfid, {
        skip: !params?.community_sfid || Boolean(params?.campaign_sfid) || (!visible && !isStakeAdmin) || campaign?.status === 'Draft'
    });

    const [
        updateConcessionSavingByPropertySfid,
        {data: updatedPropertyValues, isSuccess: isPropertyApplySuccess, isLoading: isPropertyApplyLoading, isFetching: isPropertyApplyFetching}
    ] = useUpdateConcessionSavingByPropertySfidMutation();

    useEffect(() => {
        if (campaign) {
            setVisible(!campaign?.hidden_loyalty_cloud_features__c?.includes('concessions_savings'));
        } else if (community) {
            setVisible(!community?.hidden_loyalty_cloud_features__c?.includes('concessions_savings'));
        }
    }, [campaign, community]);

    const handleApply = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (dashboardCampaign.sfid && dashboardCampaign.sfid !== 'all') {
            updateConcessionSavingByCampaignSfid({sfid: dashboardCampaign.sfid, params: editedValues});
        } else if (dashboardCommunity.sfid && dashboardCommunity.sfid !== 'all') {
            updateConcessionSavingByPropertySfid({sfid: dashboardCommunity.sfid, params: editedValues});
        }
    };

    const concessionData = useMemo(() => {
        const defaultResponse = {is_visible: false, is_loading: false};

        if (!visible && !isStakeAdmin) return defaultResponse;
        if (isLoading || isFetching || isPropertyFetching || isPropertyLoading) return {is_loading: true};

        if (isApplySuccess) {
            return updatedValues?.is_supported === false ? {...data, is_visible: true, is_loading: false} : {...updatedValues, is_visible: true, is_loading: false};
        }
        if (isPropertyApplySuccess) {
            return updatedPropertyValues?.is_supported === false
                ? {...data, is_visible: true, is_loading: false}
                : {...updatedPropertyValues, is_visible: true, is_loading: false};
        }
        if (isSuccess && params?.campaign_sfid) {
            return data?.is_supported === false ? defaultResponse : {...data, is_visible: true, is_loading: false};
        }
        if (isPropertySuccess && params?.community_sfid) {
            return propertyData?.is_supported === false ? defaultResponse : {...propertyData, is_visible: true, is_loading: false};
        }
        return defaultResponse;
    }, [
        params,
        visible,
        isStakeAdmin,
        isLoading,
        isFetching,
        isPropertyFetching,
        isPropertyLoading,
        isApplySuccess,
        updatedValues,
        isPropertyApplySuccess,
        updatedPropertyValues,
        isSuccess,
        isPropertySuccess,
        data,
        propertyData
    ]);

    if (campaign?.status === 'Draft') {
        return (
            <Box sx={{p: 2, width: '100%', border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white}}>
                <Box display="flex" justifyContent="center" alignItems="center" sx={{height: '100px'}}>
                    <Typography variant="subtitle1" color="text.secondary">
                        Concession comps data is not available for draft campaigns.
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (concessionData?.is_loading) {
        return (
            <Box sx={{p: 2, width: '100%', border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white}}>
                <LoadingBox />
            </Box>
        );
    }

    if (concessionData?.is_visible === false) {
        if (isError || isPropertyError) {
            return (
                <Box sx={{p: 2, width: '100%', border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white}}>
                    <Typography variant="body2" color="error.main" sx={{textAlign: 'center', py: 4}}>
                        Something went wrong.
                    </Typography>
                </Box>
            );
        }
        return (
            <Box sx={{p: 2, width: '100%', border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white}}>
                <Box display="flex" justifyContent="center" alignItems="center" sx={{height: '100px'}}>
                    <Typography variant="subtitle1" color="text.secondary">
                        No concession comps data available.
                    </Typography>
                </Box>
            </Box>
        );
    }

    let savings = null;
    if (isApplyFetching || isApplyLoading || isPropertyApplyFetching || isPropertyApplyLoading) {
        savings = <LoadingBox />;
    } else if (isSuccess || isApplySuccess || isPropertySuccess || isPropertyApplySuccess) {
        savings = (
            <>
                <Savings data={concessionData.current_market_data} mainInfo={concessionData.projected_savings} />
                <Savings data={concessionData.historic_market_data} mainInfo={concessionData.historic_savings} />
            </>
        );
    }

    return (
        <Box sx={{p: 2, width: '100%', border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white}}>
            <Stack direction="column" rowGap={2} divider={<Box sx={{borderTop: `1px solid ${tokens.color.neutral200}`}} />}>
                <ConcessionsHeader data={concessionData.concessions} />
                <ConcessionsInfo data={concessionData.comp_filters} editedValues={editedValues} setEditedValues={setEditedValues} handleApply={handleApply} />
                {savings}
                {isStakeAdmin && (
                    <VisibilitySwitch
                        id={dashboardCampaign?.sfid}
                        accountName={dashboardAccount?.name}
                        setVisible={setVisible}
                        isVisible={visible}
                        modelType="campaign"
                        featureType="concessions_savings"
                    />
                )}
            </Stack>
        </Box>
    );
};

export default ConcessionComps;
